import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import '@/styles/index.css';
import App from './App';
import { DOWNLOAD_LINKS } from '@/travo/config';
import { pageChunks } from './sections';

// Visits are counted by Cloudflare Web Analytics, which Cloudflare Pages injects (no cookies, no code here).

/**
 * Store-link subdomains only redirect. index.html hides the page on these hosts before
 * paint (keep its host list in step with this map) so the prerendered home never flashes.
 */
const STORE_SUBDOMAINS: Record<string, (isAndroid: boolean) => string> = {
  'app.': (isAndroid) => (isAndroid ? DOWNLOAD_LINKS.playStore : DOWNLOAD_LINKS.appStore),
  'appstore.': () => DOWNLOAD_LINKS.appStore,
  'googleplay.': () => DOWNLOAD_LINKS.playStore
};

/** Redirects store-link subdomains to their store; returns true if leaving. */
const redirectStoreSubdomain = (): boolean => {
  const host = window.location.hostname;
  const prefix = Object.keys(STORE_SUBDOMAINS).find((p) => host.startsWith(p));
  if (!prefix) return false;
  window.location.replace(STORE_SUBDOMAINS[prefix](/android/i.test(navigator.userAgent)));
  return true;
};

const mount = () => {
  const root = document.getElementById('root')!;
  const app = (
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>
  );

  // Prerendered routes (scripts/prerender.mjs) ship HTML to hydrate; `yarn dev` serves an empty root.
  if (root.hasChildNodes()) {
    hydrateRoot(root, app);
  } else {
    createRoot(root).render(app);
  }
};

if (!redirectStoreSubdomain()) {
  // The prerendered HTML holds the page's section (and a legal page's full text): load those chunks
  // before hydrating so the first render matches. If one fails, the prerendered page stays as it is,
  // readable but not interactive.
  Promise.all(pageChunks(window.location.pathname).map((chunk) => chunk.load())).then(mount, (error: unknown) =>
    console.error('Could not load the page', error)
  );
}
